import { Component, Injector, Input, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { WidgetBaseComponent } from '../../widgetBase';
import { ObjectUtil } from '../../../../../components/common/object-util/object-util';

/**
 * OwnershipAndTransactionHistory Widget
 */
@Component({
  selector: 'ownership-and-transaction-history-widget',
  templateUrl: './ownership-and-transaction-history-widget.component.html',
  styleUrls: ['../../common/css/general.scss', '../../common/css/text-and-list.scss', './ownership-and-transaction-history-widget.component.scss']
})
export class OwnershipAndTransactionHistoryWidgetComponent extends WidgetBaseComponent implements OnInit {

  /**
   * Widget Guid
   */
  @Input() widgetGuid: string;

  /**
   * Should custom labels be disabled?
   */
  disableCustomLabels = true;

  /**
   * Delete current last sale row subject
   */
  private deleteCurrentRowSubjectLastSale = new Subject<void>();

  /**
   * Delete current last sale row observable
   */
  public deleteCurrentRowLastSale$ = this.deleteCurrentRowSubjectLastSale.asObservable();

  /**
   * Add last sale row subject
   */
  private addRowSubjectLastSale = new Subject<any>();

  /**
   * Add last sale row observable
   */
  public addRowLastSale$ = this.addRowSubjectLastSale.asObservable();

  /**
   * Move current last sale row up subject
   */
  private moveCurrentRowUpSubjectLastSale = new Subject<void>();

  /**
   * Move current last sale row up observable
   */
  public moveCurrentRowUpLastSale$ = this.moveCurrentRowUpSubjectLastSale.asObservable();

  /**
   * Move current last sale row down subject
   */
  private moveCurrentRowDownSubjectLastSale = new Subject<void>();

  /**
   * Move current last sale row down observable
   */
  public moveCurrentRowDownLastSale$ = this.moveCurrentRowDownSubjectLastSale.asObservable();

  /**
   * Text & List section rows
   */
  rowsLastSale: any = [];

  /**
   * Text & List section name
   */
  readonly textAndListSectionNameLastSale: string;

  /**
  * Constructor
  */
  constructor(injector: Injector) {
    super(injector, 'ownershipAndTransactionHistory', false);
    this.textAndListSectionNameLastSale = `${this.name}-lastsale`;
  }

  /**
   * Layout Id
   */
  get layoutId() {
    const layout: string = this.widgetConfig.widgetSettings.currentLayout;
    const parts = layout.split(' ');
    const layoutId = parts[parts.length - 1];//Sale 1 => layoutId = 1, etc..
    return parseInt(layoutId.trim());//trim and make int
  }

  /**
   * Initializes the component
   */
  ngOnInit() {
    // will call registerEvents and onInitialized when config (this.widgetConfig and this.page) is ready

    this.initWidgetConfig();
    this.widgetResized();
  }

  /**
   * Called by base class to let widget register to various app events (like saving, loading, resizing etc)
   */
  registerEvents(): void {
    this.useDefaultServiceSaveChangesHandler("ownershipAndTransactionHistory", this.provideSaveObject.bind(this));
  }

  /**
   * Called by base class to inform widget that it is initialized
   * Mostly used to define where widget data should be loaded from
   */
  onInitialized(): void {
    if (!this.widgetConfig.hashtagSupported) {
      this.widgetConfig.hashtagSupported = true;
    }
    this.initializeLastSaleSection();

    this.loadReflist('ref_l3_country_specifics_last_sale', !this.disableCustomLabels);

    this.mockupData$ = this.widgetService.loadMockup('ownershipAndTransactionHistory').pipe(take(1));

    this.widgetData$ = this.mockup ?
      this.mockupData$
      : this.widgetService.load('ownershipAndTransactionHistory', this.getReport(), `layoutId=${this.layoutId}`).pipe();
  }

  /**
   * Called by base class to let widget preprocess loaded data if required
   */
  protected preprocessLoadedData(data) {
    /*
     change Seller to Grantor, Buyer to Grantee and Record Identifier to Document Number
    */
    const ref = this.reflists.ref_l3_country_specifics_last_sale;
    ref.find(r => r.oid === 4).description = 'Grantor';
    ref.find(r => r.oid === 5).description = 'Grantee';
    ref.find(r => r.oid === 6).description = 'Document Number';

    // percentages are in form of 0.13 for 13% => convert
    this.percentageToUI(data, "impliedCapRate");

    return data;
  }

  /**
   * Called by base class in save process. Widget should return object to be saved
   */
  async provideSaveObject(): Promise<any> {
    const saveObject = ObjectUtil.deepCopy(this.widgetData); //Deep copy because we'd change the UI
    // percentages are in form of 0.13 for 13% => convert
    this.percentageFromUI(saveObject, "impliedCapRate");

    return saveObject;
  }

  /**
   * Initializes the Text & List section
   */
  private initializeLastSaleSection() {
    this.rowsLastSale = this.getTextAndListSettingsOrSetDefault(this.textAndListSectionNameLastSale, [
      {
        topicId: 4,
        fieldName: 'seller',
        isTextField: true,
        isNumericField: false,
        isDateField: false,
        lastTopicId: 4,
        selected: true
      }, {
        topicId: 5,
        fieldName: 'buyer',
        isTextField: true,
        isNumericField: false,
        isDateField: false,
        lastTopicId: 5,
        selected: true
      }, {
        topicId: 1,
        fieldName: 'saleDate',
        isDateField: true,
        isNumericField: false,
        isTextField: false,
        lastTopicId: 1,
        selected: true
      }, {
        topicId: 3,
        fieldName: 'salePrice',
        isNumericField: true,
        isTextField: false,
        isDateField: false,
        lastTopicId: 3,
        selected: true
      }, {
        topicId: 6,
        fieldName: 'recordIdentifier',
        isTextField: true,
        isNumericField: false,
        ifDateField: false,
        lastTopicId: 6,
        selected: true
      }, {
        topicId: 7,
        fieldName: 'impliedCapRate',
        isTextField: false,
        isNumericField: true,
        decimals: 2,
        isDateField: false,
        lastTopicId: 7,
        selected: true,
        isPercentage: true,
      }, {
        topicId: 2,
        fieldName: 'saleCondition',
        isTextField: true,
        isNumericField: false,
        isDateField: false,
        lastTopicId: 2,
        selected: true,
        leftColspan: 0,
        rightColspan: 12,
        cssClasses: ['right-column-full'],
        rightColumnAlign: 'left',
        maxlength: 102400,
        isTextArea: true,
        textAreaRows: 3
      }
    ]);
  }

  /**
   * Called by base class to inform widget about save action result
   * @param _success {boolean} The result of save action
   * @param _err {any} The error object, if save action failed
   */
  saveCompleted(_success: boolean, _err: any = null): void {
    //do something if error
  }

  /**
   * Delete current row event handler
   * @param rows {any[]} Rows
   * @param section {string} Section
   */
  public deleteCurrentRow(rows: any[], section: string): void {
    if (section === 'sale') {
      this.setTextAndListSettings(this.textAndListSectionNameLastSale, rows);
      this.deleteCurrentRowSubjectLastSale.next();
    }
  }

  /**
   * Add row event handler
   * @param rows {any[]} Rows
   * @param section {string} Section
   */
  public addRow(rows: any[], section: string): void {
    if (section === 'sale') {
      const unselectedFields = this.rowsLastSale.filter(item => !item.selected);
      if (unselectedFields.length > 0) {
        this.setTextAndListSettings(this.textAndListSectionNameLastSale, rows);
        this.addRowSubjectLastSale.next({
          topicId: this.getTopicId(section),
          fieldName: null,
          isTextField: true,
          isNumericField: false,
          isDataField: false,
          lastTopicId: this.getTopicId(section),
          selected: true
        });
      }
    }
  }

  /**
   * Move current row up event handler
   * @param rows {any[]} Rows
   * @param section {string} Section
   */
  public moveCurrentRowUp(rows: any[], section: string): void {
    if (section === 'sale') {
      this.setTextAndListSettings(this.textAndListSectionNameLastSale, rows);
      this.moveCurrentRowUpSubjectLastSale.next();
    }
  }

  /**
   * Move current row down event handler
   * @param rows {any[]} Rows
   * @param section {string} Section
   */
  public moveCurrentRowDown(rows: any[], section: string): void {
    if (section === 'sale') {
      this.setTextAndListSettings(this.textAndListSectionNameLastSale, rows);
      this.moveCurrentRowDownSubjectLastSale.next();
    }
  }

  /**
   * Data changed event handler
   * @param rows {any[]} Rows
   */
  public onDataChanged(rows: any[]): void {
    this.setTextAndListSettings(this.textAndListSectionNameLastSale, this.rowsLastSale);
    this.setDirtyFlag(true);
  }

  /**
   * Get topic Id
   */
  private getTopicId(section: string) {
    if (section === 'sale') {
      const unselectedFields = this.rowsLastSale.filter(item => !item.selected);
      return Math.min(...unselectedFields.map(item => item.topicId));
    }
  }

  /**
   * Layout change event handler
   * @param newLayout The new layout
   */
  public layoutChanged(newLayout: string): void {
    super.layoutChanged(newLayout);
    this.widgetData.layoutId = this.layoutId;

    this.widgetData$ = this.mockup ?
      this.mockupData$
      : this.widgetService.load('ownershipAndTransactionHistory', this.getReport(), `layoutId=${this.layoutId}`).pipe();

    this.reloadData();
    this.setDirtyFlag(true, true);//force dirty even if in not edit mode (as layout can be changed without checking out widget)
  }
}
